import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "../../../lib/supabase";
import MetricsCards from "./components/MetricsCards";
import LeadsTable from "./components/LeadsTable";
import SubscriptionsTable from "./components/SubscriptionsTable";
import PhonePoolPanel from "./components/PhonePoolPanel";
import FollowupPanel from "./components/FollowupPanel";
import MRRChart from "./components/MRRChart";

type Tab = "leads" | "subscriptions" | "pool" | "followup" | "mrr";

interface Lead {
  id: string;
  name: string;
  email: string;
  business: string;
  phone: string | null;
  source: string | null;
  email_sent: boolean;
  created_at: string;
}

interface Subscription {
  id: string;
  email: string;
  plan_id: string;
  status: string;
  billing: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  created_at: string;
  assigned_phone: string | null;
  setup_completed: boolean;
  preferred_lang: string | null;
  admin_notes: string | null;
  cancellation_reason: string | null;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("leads");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(true);
  const [loadingSubscriptions, setLoadingSubscriptions] = useState(true);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [subsFilter, setSubsFilter] = useState<"all" | "no_phone" | "setup_pending">("all");

  useEffect(() => {
    async function checkAuth() {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/admin/login"); return; }

      const { data: adminRow } = await supabase.from("admins").select("email").maybeSingle();
      if (!adminRow) { await supabase.auth.signOut(); navigate("/admin/login"); return; }

      setUserEmail(session.user.email ?? null);
      setAuthChecked(true);
    }
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    if (!authChecked) return;
    fetchLeads();
    fetchSubscriptions();
  }, [authChecked]);

  async function fetchLeads() {
    setLoadingLeads(true);
    const { data } = await supabase
      .from("leads")
      .select("id, name, email, business, phone, source, email_sent, created_at")
      .order("created_at", { ascending: false });
    setLeads(data ?? []);
    setLoadingLeads(false);
  }

  async function fetchSubscriptions() {
    setLoadingSubscriptions(true);
    const { data } = await supabase
      .from("subscriptions")
      .select("id, email, plan_id, status, billing, current_period_end, cancel_at_period_end, created_at, assigned_phone, setup_completed, preferred_lang, admin_notes, cancellation_reason")
      .order("created_at", { ascending: false });
    setSubscriptions(data ?? []);
    setLoadingSubscriptions(false);
  }

  async function handleAssignPhone(
    id: string,
    email: string,
    planId: string,
    phone: string,
    lang: string
  ) {
    await supabase
      .from("subscriptions")
      .update({ assigned_phone: phone })
      .eq("id", id);

    setSubscriptions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, assigned_phone: phone } : s))
    );

    await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/notify-phone-assigned`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ email, plan_id: planId, assigned_phone: phone, lang }),
      }
    );
  }

  async function handleSaveNote(id: string, note: string) {
    await supabase
      .from("subscriptions")
      .update({ admin_notes: note })
      .eq("id", id);

    setSubscriptions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, admin_notes: note } : s))
    );
  }

  async function handleSendReminder(
    _id: string,
    email: string,
    planId: string,
    assignedPhone: string | null,
    lang: string
  ) {
    await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/send-setup-reminder`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          plan_id: planId,
          assigned_phone: assignedPhone,
          lang,
        }),
      }
    );
  }

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/admin/login");
  };

  const activeSubscriptions = subscriptions.filter((s) => s.status === "active").length;
  const emailsSent = leads.filter((l) => l.email_sent).length;
  const cancelPending = subscriptions.filter((s) => s.cancel_at_period_end).length;
  const noPhoneCount = subscriptions.filter((s) => s.status === "active" && !s.assigned_phone).length;
  const noSetupCount = subscriptions.filter((s) => s.status === "active" && !s.setup_completed).length;

  function handleAlertFilterNoPhone() {
    setTab("subscriptions");
    setSubsFilter("no_phone");
  }

  function handleAlertFilterNoSetup() {
    setTab("subscriptions");
    setSubsFilter("setup_pending");
  }

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center gap-3 text-stone-400">
        <i className="ri-loader-4-line animate-spin text-2xl" />
        <span className="text-sm">Verificando acceso...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <meta name="robots" content="noindex, nofollow" />
      {/* Top nav */}
      <nav className="w-full bg-white border-b border-stone-100 px-8 py-0 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="cursor-pointer">
            <img
              src="https://static.readdy.ai/image/464d6b2d88c2c0b329c43db37f7a6b21/5e3d82f418168fbdbc49efd95d2ee821.png"
              alt="Auria"
              className="h-7 w-auto"
            />
          </Link>
          <span className="h-4 w-px bg-stone-200" />
          <div className="flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-100 rounded-full">
            <i className="ri-shield-check-fill text-amber-500 text-xs" />
            <span className="text-amber-700 text-xs font-bold uppercase tracking-widest">Admin</span>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-stone-500">
            <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center">
              <i className="ri-user-3-line text-stone-500 text-sm" />
            </div>
            <span className="text-sm">{userEmail}</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 text-sm text-stone-500 hover:text-stone-800 hover:bg-stone-100 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-logout-box-line text-sm" />
            Cerrar sesión
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="px-8 py-8 max-w-screen-xl mx-auto space-y-6">
        {/* Page title */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Panel de control
            </h1>
            <p className="text-stone-400 text-sm mt-0.5">Vista general de Auria · {new Date().toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
          </div>
          <button
            onClick={() => { fetchLeads(); fetchSubscriptions(); }}
            className="flex items-center gap-2 px-4 py-2 border border-stone-200 rounded-xl text-sm text-stone-600 hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line text-sm" />
            Actualizar datos
          </button>
        </div>

        {/* Metrics */}
        <MetricsCards
          totalLeads={leads.length}
          activeSubscriptions={activeSubscriptions}
          emailsSent={emailsSent}
          cancelPending={cancelPending}
          noPhoneCount={noPhoneCount}
          noSetupCount={noSetupCount}
          onFilterNoPhone={handleAlertFilterNoPhone}
          onFilterNoSetup={handleAlertFilterNoSetup}
        />

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1 w-fit flex-wrap">
          {(["leads", "subscriptions", "pool", "followup", "mrr"] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                tab === t
                  ? "bg-white text-stone-900 shadow-sm"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {t === "leads" ? (
                <span className="flex items-center gap-2">
                  <i className="ri-user-add-line text-sm" />
                  Leads ({leads.length})
                </span>
              ) : t === "subscriptions" ? (
                <span className="flex items-center gap-2">
                  <i className="ri-vip-crown-line text-sm" />
                  Suscripciones ({subscriptions.length})
                </span>
              ) : t === "pool" ? (
                <span className="flex items-center gap-2">
                  <i className="ri-phone-line text-sm" />
                  Bolsa de números
                </span>
              ) : t === "followup" ? (
                <span className="flex items-center gap-2">
                  <i className="ri-mail-send-line text-sm" />
                  Seguimiento
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <i className="ri-line-chart-line text-sm" />
                  MRR
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tables */}
        {tab === "leads" ? (
          <LeadsTable leads={leads} loading={loadingLeads} />
        ) : tab === "subscriptions" ? (
          <SubscriptionsTable
            subscriptions={subscriptions}
            loading={loadingSubscriptions}
            onAssignPhone={handleAssignPhone}
            onSaveNote={handleSaveNote}
            onSendReminder={handleSendReminder}
            externalFilter={subsFilter}
          />
        ) : tab === "pool" ? (
          <PhonePoolPanel />
        ) : tab === "followup" ? (
          <FollowupPanel />
        ) : (
          <MRRChart subscriptions={subscriptions} />
        )}

        {/* Footer */}
        <div className="pt-4 pb-8 border-t border-stone-100 flex items-center justify-between text-xs text-stone-400">
          <p>Auria Admin Panel · Solo acceso autorizado</p>
          <Link to="/" className="hover:text-stone-600 transition-colors cursor-pointer">
            Volver a la web pública
          </Link>
        </div>
      </main>
    </div>
  );
}
